import { z } from 'zod';
import { ulidSchema } from './id';

/**
 * Undo and redo — the shared route contract for B45's backend surface.
 *
 * `FR-EXP-013` is deliberately client-session scoped: the browser owns the
 * stack order and depth, while the backend applies undo or redo for one
 * activity entry id the client already holds. There is no server-side stack
 * contract here and no generic event replay surface.
 */

export const undoRedoModeSchema = z.enum(['undo', 'redo']);

export type UndoRedoMode = z.infer<typeof undoRedoModeSchema>;

export const undoOperationSchema = z.enum([
  'card_move',
  'card_archive',
  'list_archive',
  'card_title',
  'card_label',
  'card_member',
  'card_start_date',
  'card_due_date',
  'card_merge',
]);

export type UndoOperation = z.infer<typeof undoOperationSchema>;

/** `POST /activity/:id/undo` and `POST /activity/:id/redo` path params. */
export const undoRedoActivityParamsSchema = z.strictObject({
  id: ulidSchema,
});

export type UndoRedoActivityParams = z.infer<typeof undoRedoActivityParamsSchema>;

/**
 * Both routes are bodyless. The strict empty object makes an accidental
 * client-side option a validation failure instead of a silently ignored field.
 */
export const undoRedoRequestBodySchema = z.strictObject({});

export type UndoRedoRequestBody = z.infer<typeof undoRedoRequestBodySchema>;

/** Shared receipt fields for undo/redo responses. */
const undoRedoReceiptFields = {
  sourceActivityEntryId: ulidSchema,
  appliedActivityEntryIds: z.array(ulidSchema).min(1),
  boardId: ulidSchema,
  cardId: ulidSchema.optional(),
  operation: undoOperationSchema,
};

/** `POST /activity/:id/undo` response. */
export const undoActivityResponseSchema = z.strictObject({
  mode: z.literal('undo'),
  ...undoRedoReceiptFields,
});

export type UndoActivityResponse = z.infer<typeof undoActivityResponseSchema>;

/** `POST /activity/:id/redo` response. */
export const redoActivityResponseSchema = z.strictObject({
  mode: z.literal('redo'),
  ...undoRedoReceiptFields,
});

export type RedoActivityResponse = z.infer<typeof redoActivityResponseSchema>;

/** Either route receipt, useful for clients that share one handler. */
export const undoRedoActivityResponseSchema = z.discriminatedUnion('mode', [
  undoActivityResponseSchema,
  redoActivityResponseSchema,
]);

export type UndoRedoActivityResponse = z.infer<typeof undoRedoActivityResponseSchema>;
